import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Avatar, createStyles, Group, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {agentHomepageUrl, chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import type {Routine} from 'llm/routines/RoutineTypes';
import useRoutines from 'llm/routines/useRoutines';
import {getAvatarInitials} from 'ui-library/avatar/Avatar';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import SidebarSectionHeader from './SidebarSectionHeader';

const AVATAR_SIZE_PX = 32;
const MAX_VISIBLE_AGENTS = 5;
const PILL_MAX_WIDTH_PX = 180;

/** Shown when an agent has no usable name, so its pill still has a stable label/tooltip/initials. */
const PLACEHOLDER_AGENT_NAME = 'Untitled Agent';

const useStyles = createStyles((theme) => ({
  root: {
    marginBottom: theme.spacing.xs,
  },
  pillGroup: {
    marginLeft: 4,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.colors.violet[0],
    padding: '2px 12px 2px 2px',
    maxWidth: PILL_MAX_WIDTH_PX,
    minWidth: 0,
    '&:hover': {
      backgroundColor: theme.colors.violet[1],
    },
  },
  newAgentPill: {
    backgroundColor: theme.colors.indigo[0],
    '&:hover': {
      backgroundColor: theme.colors.indigo[1],
    },
  },
  avatar: {
    flexShrink: 0,
  },
  avatarPlaceholder: {
    backgroundColor: theme.white,
  },
  viewAll: {
    width: AVATAR_SIZE_PX,
    height: AVATAR_SIZE_PX,
    minWidth: AVATAR_SIZE_PX,
    color: theme.colors.dark[7],
  },
}));

/**
 * The agent's display name, normalizing null/blank/whitespace-only names to a placeholder so every
 * pill has a non-empty visible label, accessible name, and tooltip.
 */
export function getAgentDisplayName(name: string | null): string {
  const trimmed = name?.trim() ?? '';
  return trimmed === '' ? PLACEHOLDER_AGENT_NAME : trimmed;
}

function getTimestamp(value: string): number {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function AgentPill({routine}: {routine: Routine}): JSX.Element {
  const {classes} = useStyles();
  const displayName = getAgentDisplayName(routine.name);
  const handleClick = useCallback(() => {
    navigateToURL(agentHomepageUrl(routine.id));
  }, [routine.id]);

  return (
    <Tooltip label={displayName} withinPortal={true} openDelay={300} position="top">
      <UnstyledButton
        type="button"
        onClick={handleClick}
        className={classes.pill}
        data-amp-safe={false}
        {...getTestProps({component: 'SidebarAgentsList', element: 'agentPill', key: routine.id})}
      >
        <Avatar
          aria-hidden={true}
          className={classes.avatar}
          color="violet"
          radius="xl"
          size={AVATAR_SIZE_PX}
          classNames={{placeholder: classes.avatarPlaceholder}}
        >
          {getAvatarInitials({name: displayName})}
        </Avatar>
        <Text size="sm" truncate={true}>
          {displayName}
        </Text>
      </UnstyledButton>
    </Tooltip>
  );
}

/**
 * Recently created/updated agent (routine) pills for the redesigned chat sidebar. Up to five agents
 * wrap to fit the drawer width, followed by a chevron that opens the full ordered Agent list when
 * configured agents exist. Each pill deep-links to that agent's homepage; the leading New agent
 * pill opens Agent creation. Renders nothing while loading.
 *
 * Callers gate mounting on `isAgentsFeatureEnabled() && llmEnableRoutineHomepage`; this component
 * does not re-check those flags.
 */
export default function SidebarAgentsList({
  onViewAllAgents,
}: {
  onViewAllAgents: (orderedRoutines: Array<Routine>) => void;
}): JSX.Element | null {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  // Reuses the shared, cached routines hook. Its payload includes fields this strip doesn't render
  // (message blocks, trigger config, timestamps); since RoutinesQuery is usually already warm from
  // other agent surfaces, that's acceptable for now. TODO(BNCH-171285): if this always-mounted strip
  // proves costly, back it with a slim id/name-only projection instead.
  const {routines, isLoading} = useRoutines();

  const handleCreateAgent = useCallback(() => {
    navigateToURL(chatSettingsModalUrl(ChatSettingsTab.AGENTS, {createNew: true}));
  }, []);

  // TODO(BNCH-172613): extend sorting to factor in recent runs, not just create/edit time.
  const orderedRoutines = useMemo(
    () =>
      [...routines].sort((left, right) => {
        const leftActivity = Math.max(getTimestamp(left.createdAt), getTimestamp(left.modifiedAt));
        const rightActivity = Math.max(getTimestamp(right.createdAt), getTimestamp(right.modifiedAt));
        return rightActivity - leftActivity || left.id.localeCompare(right.id);
      }),
    [routines]
  );

  const handleViewAllAgents = useCallback(() => {
    onViewAllAgents(orderedRoutines);
  }, [onViewAllAgents, orderedRoutines]);

  if (isLoading) {
    return null;
  }

  return (
    <div className={classes.root} {...getTestProps({component: 'SidebarAgentsList', element: 'root'})}>
      <SidebarSectionHeader>Agents</SidebarSectionHeader>
      <Group
        spacing="xs"
        className={classes.pillGroup}
        {...getTestProps({component: 'SidebarAgentsList', element: 'pillGroup'})}
      >
        <UnstyledButton
          type="button"
          onClick={handleCreateAgent}
          className={cx(classes.pill, classes.newAgentPill)}
          {...getTestProps({component: 'SidebarAgentsList', element: 'addAgent'})}
        >
          <Avatar aria-hidden={true} color="blue" radius="xl" size={AVATAR_SIZE_PX} variant="filled">
            <BenchlingIcon icon="plus" />
          </Avatar>
          <Text size="sm">New agent</Text>
        </UnstyledButton>
        {orderedRoutines.slice(0, MAX_VISIBLE_AGENTS).map((routine) => (
          <AgentPill key={routine.id} routine={routine} />
        ))}
        {orderedRoutines.length > 0 && (
          <Tooltip label="View all agents" withinPortal={true} openDelay={300} position="top">
            <ActionIcon
              aria-label="View all agents"
              className={classes.viewAll}
              onClick={handleViewAllAgents}
              variant="subtle"
              {...getTestProps({component: 'SidebarAgentsList', element: 'viewAllAgents'})}
            >
              <BenchlingIcon icon="chevron-right" />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </div>
  );
}
